import React, { useState, useEffect, useRef } from 'react';
import { fetchBookstore } from '../utils/bookstore';
import Loader from './Loader';
import Drawer from './Drawer';
import CRNSectionCard from './CRNSectionCard';
import {
    createMarkup,
    getCriticalDatesForItem
} from '../utils/formatters';
import CRNCentricSectionCard from './CRNCentricSectionCard';
import CRNTestCard from './CRNTestCard';
import CRNColumns from './CRNColumns';

function SectionCards({ data, loading, selectedTerm }) {
    const [visibleData, setVisibleData] = useState([]);
    const [visibleCount, setVisibleCount] = useState(50);
    const loadMoreRef = useRef();

    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedCRN, setSelectedCRN] = useState(null);

    const [bookstoreInfo, setBookstoreInfo] = useState(null);
    const [bookstoreLoading, setBookstoreLoading] = useState(false);

    const [instructorDrawerOpen, setInstructorDrawerOpen] = useState(false);
    const [instructorData, setInstructorData] = useState(null);

    useEffect(() => {
        if (drawerVisible && selectedCRN?.termCode && selectedCRN?.crn) {
            setBookstoreLoading(true);
            fetchBookstore(selectedCRN.termCode, selectedCRN.crn).then(info => {
                setBookstoreInfo(info);
                setBookstoreLoading(false);
            });
        }
    }, [drawerVisible, selectedCRN]);

    useEffect(() => {
        if (!Array.isArray(data)) return;
        const sorted = [...data].sort((a, b) => {
            const subjA = a.subjCode || '';
            const subjB = b.subjCode || '';
            if (subjA !== subjB) return subjA.localeCompare(subjB);

            const extractParts = (str) => {
                if (!str) return [1, '', 0, ''];
                const match = str.match(/^([A-Z]*)(\d+)([A-Z]*)$/i);
                if (!match) return [1, '', 0, str];
                const [_, prefix, digits, suffix] = match;
                const isAlphaPrefix = /^[A-Z]/i.test(str) ? 0 : 1;
                return [isAlphaPrefix, prefix || '', parseInt(digits), suffix || ''];
            };

            const [flagA, prefixA, numA, suffixA] = extractParts(a.courseNumbDisplay || '');
            const [flagB, prefixB, numB, suffixB] = extractParts(b.courseNumbDisplay || '');

            if (flagA !== flagB) return flagA - flagB;
            if (prefixA !== prefixB) return prefixA.localeCompare(prefixB);
            if (numA !== numB) return numA - numB;
            return suffixA.localeCompare(suffixB);
        });

        setVisibleData(sorted.slice(0, visibleCount));
    }, [data, visibleCount]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && visibleCount < data.length) {
                    setVisibleCount((prev) => Math.min(prev + 50, data.length));
                }
            },
            { threshold: 1.0 }
        );
        const currentLoadMoreRef = loadMoreRef.current;
        if (currentLoadMoreRef) {
            observer.observe(currentLoadMoreRef);
        }
        return () => {
            if (currentLoadMoreRef) {
                observer.unobserve(currentLoadMoreRef);
            }
        };
    }, [data.length, visibleCount]);


    const onlinePop = {
        "Online ASYNCH": "Fully online and taught primarily through Canvas and other learning platforms. Classes do not have scheduled class meetings. Students will independently participate through online work and assignments provided by the instructor.",
        "Online ZOOM": "Fully online and taught through Canvas with required attendance at live, online Zoom meetings during scheduled class times."
    };

    const groupedVisibleData = visibleData.reduce((acc, item) => {
        const key = `${item.subjCode || 'N/A'} ${item.courseNumbDisplay || 'N/A'} - ${item.courseTitle || 'Untitled Course'}`;
        if (!acc[key]) {
            acc[key] = {
                description: item.description,
                sectionNote: item.sectionNote,
                courseNumbDisplay: item.courseNumbDisplay,
                crnGroups: {},
                subjDesc: item.subjDesc || 'N/A',
            };
        }
        if (!acc[key].crnGroups[item.crn]) {
            acc[key].crnGroups[item.crn] = [];
        }
        acc[key].crnGroups[item.crn].push(item);
        return acc;
    }, {});

    const subjectGroupedData = {};
    Object.entries(groupedVisibleData).forEach(([courseKey, group]) => {
        const subjDesc = group.subjDesc || 'N/A';
        if (!subjectGroupedData[subjDesc]) {
            subjectGroupedData[subjDesc] = {};
        }
        subjectGroupedData[subjDesc][courseKey] = group;
    });


    return (
        <div className="flex-1 p-4 bg-pcc-lighter-gray">
            {loading ? <Loader /> : (
                <>
                    {visibleData.length === 0 && !loading ? (
                        <p className="text-center text-pcc-dark-gray py-10">
                            {data.length === 0 && !selectedTerm
                                ? 'Please select a term.'
                                : 'No sections found matching your criteria.'}
                        </p>
                    ) : (
                        <div>
                            {Object.entries(subjectGroupedData).map(([subject, courses], subjectIdx) => (
                                <div
                                    key={subjectIdx}
                                    className="mb-12 rounded-lg border border-pcc-light-gray bg-white shadow-md"
                                >
                                    <div className="sticky top-0 z-10 bg-gray-100 px-6 py-4 border-b border-pcc-light-gray shadow-sm">
                                        <h2 className="text-xl font-bold text-pcc-dark-gray">{subject}</h2>
                                    </div>

                                    {/* Content Area for Courses */}
                                    <div className="p-4 md:p-6 space-y-6">
                                        {Object.entries(courses).map(([courseKey, group], groupIdx) => (
                                            <div key={groupIdx} className="border border-gray-300 rounded-lg bg-gray-50 shadow-sm">
                                                <div className="sticky z-8 bg-gray-200 px-4 py-3 font-semibold text-gray-700 border-b border-gray-300 shadow-sm"
                                                    style={{ top: 60 }}>
                                                    <span className="text-base">{courseKey}</span>
                                                </div>
                                                {(group.description || group.sectionNote) && (
                                                    <div className="px-4 py-3 bg-white text-sm">
                                                        {group.sectionNote && (<div className="text-gray-700" dangerouslySetInnerHTML={createMarkup(group.sectionNote)}></div>)}
                                                    </div>
                                                )}
                                                <div className="bg-white px-2">
                                                    {Object.entries(group.crnGroups)
                                                        .sort(([, aSections], [, bSections]) => {
                                                            const aTime = aSections[0]?.beginTime;
                                                            const bTime = bSections[0]?.beginTime;

                                                            if (!aTime && !bTime) return 0;
                                                            if (!aTime) return 1;
                                                            if (!bTime) return -1;

                                                            return aTime.localeCompare(bTime);
                                                        })
                                                        .map(([crn, sections]) => (
                                                            <CRNSectionCard
                                                                key={crn}
                                                                crn={crn}
                                                                sections={sections}
                                                                onlinePop={onlinePop}
                                                                setDrawerVisible={setDrawerVisible}
                                                                setSelectedCRN={setSelectedCRN}
                                                                setInstructorDrawerOpen={setInstructorDrawerOpen}
                                                                setInstructorData={setInstructorData}
                                                            />
                                                        ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {visibleCount < data.length && !loading && (
                        <div ref={loadMoreRef} className="h-20 flex justify-center items-center text-gray-500">Loading more sections...</div>
                    )}
                </>
            )}

            <Drawer
                isOpen={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                title={selectedCRN && `${selectedCRN.subjCode || ''} ${selectedCRN.courseNumbDisplay || selectedCRN.crseNumb || ''} - CRN ${selectedCRN.crn}`}
            >
                {selectedCRN && (
                    <div className="text-gray-700 space-y-6 px-1 h-full overflow-y-auto shadow-inner">

                        {selectedCRN.description && (
                            <section>
                                <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-2">Section Information</h4>
                                <div className="prose prose-sm max-w-none text-gray-600" dangerouslySetInnerHTML={createMarkup(selectedCRN.description)} />
                            </section>
                        )}

                        {bookstoreLoading ? (
                            <section className="pt-4 border-t border-gray-200">
                                <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-2">Textbooks</h4>
                                <div className="flex items-center text-sm text-gray-600 gap-2">
                                    <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Loading textbook information...
                                </div>
                            </section>
                        ) : (
                            <section className="pt-4 border-t border-gray-200">
                                <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-2">Textbooks</h4>
                                {bookstoreInfo && bookstoreInfo !== 'No Text Book' ? (
                                    <div className="space-y-1 text-sm text-gray-700">
                                        {bookstoreInfo.title && <p><strong>Title:</strong> {bookstoreInfo.title}</p>}
                                        {bookstoreInfo.edition && <p><strong>Edition:</strong> {bookstoreInfo.edition}</p>}
                                        {bookstoreInfo.author && <p><strong>Author:</strong> {bookstoreInfo.author}</p>}
                                        {bookstoreInfo.publisher && <p><strong>Publisher:</strong> {bookstoreInfo.publisher}</p>}
                                        {bookstoreInfo.link && (
                                            <a
                                                href={bookstoreInfo.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center text-blue-600 hover:text-blue-700 hover:underline"
                                            >
                                                View Required Textbooks
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                            </a>
                                        )}
                                    </div>
                                ) : (
                                    <a
                                        href={`https://www.bkstr.com/pasadenastore/follett-discover-view/booklook?shopBy=discoverViewCourseRefId&bookstoreId=2082&courseRefId=${selectedCRN.crn}&termId=${selectedCRN.termCode}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center text-blue-600 hover:text-blue-700 hover:underline text-sm"
                                    >
                                        View Required Textbooks on Pasadena Bookstore Website
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                    </a>
                                )}
                            </section>
                        )}

                        <section className="pt-4 border-t border-gray-200">
                            <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-2">Enrollment</h4>
                            <div className="space-y-1 text-sm">
                                <p>
                                    <span className="font-medium text-gray-500 w-24 inline-block">Status:</span>
                                    <span className={`font-semibold ${selectedCRN.fwSectOpenInd === 'O' ? 'text-green-600' :
                                        selectedCRN.fwSectOpenInd === 'W' ? 'text-yellow-600' :
                                            selectedCRN.fwSectOpenInd === 'C' ? 'text-red-600' :
                                                'text-gray-600'
                                        }`}>
                                        {selectedCRN.fwSectOpenInd === 'O' ? 'Open' :
                                            selectedCRN.fwSectOpenInd === 'C' ? 'Closed' :
                                                selectedCRN.fwSectOpenInd === 'W' ? 'Waitlist Available' :
                                                    selectedCRN.fwSectOpenInd === 'R' ? 'Reserved' : 'Unknown'}
                                    </span>
                                </p>
                                <p>
                                    <span className="font-medium text-gray-500 w-24 inline-block">Enrolled:</span>
                                    {selectedCRN.enrl ?? 'N/A'} / {selectedCRN.maxEnrl ?? 'N/A'}
                                </p>
                                {(selectedCRN.fwSectOpenInd === 'W' || (selectedCRN.waitCapacity && selectedCRN.waitCapacity > 0)) && (
                                    <p>
                                        <span className="font-medium text-gray-500 w-24 inline-block">Waitlist:</span>
                                        {selectedCRN.waitCount ?? 'N/A'} / {selectedCRN.waitCapacity ?? 'N/A'}
                                    </p>
                                )}
                            </div>
                        </section>

                        <section className="pt-4 border-t border-gray-200">
                            <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-3">Critical Dates</h4>
                            <div className="space-y-1.5 text-sm">
                                {getCriticalDatesForItem(selectedCRN)
                                    .filter(date => date.value && date.value !== 'N/A')
                                    .map((dateInfo, idx) => (
                                        <div key={`drawer-crit-date-${idx}`} className="flex justify-between items-center">
                                            <span className="text-gray-600">{dateInfo.label}:</span>
                                            <span className=" px-2 py-0.5 rounded text-xs font-mono text-gray-800">{dateInfo.value}</span>
                                        </div>
                                    ))
                                }
                            </div>
                        </section>

                    </div>
                )}
            </Drawer>
            <Drawer
                isOpen={instructorDrawerOpen}
                onClose={() => setInstructorDrawerOpen(false)}
                title="Instructor Information"
            >
                <div className="text-gray-700 space-y-6 px-3 py-4 h-full overflow-y-auto shadow-inner">
                    {instructorData?.primary && (
                        <section>
                            <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-2">
                                Primary Instructor
                            </h4>
                            <div className="space-y-1 text-sm text-gray-800">
                                <p className="font-medium">{instructorData.primary.name}</p>
                                {instructorData.primary.email && (
                                    <div className="flex items-center space-x-2">
                                        <span className="text-xs text-gray-600">{instructorData.primary.email}</span>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {instructorData?.secondary?.length > 0 && (
                        <section className="pt-4 border-t border-gray-200">
                            <h4 className="uppercase tracking-wide text-sm font-semibold text-pcc-dark-gray mb-2">
                                Additional Instructor(s)
                            </h4>
                            <div className="space-y-3 text-sm text-gray-800">
                                {instructorData.secondary.map((instructor, idx) => (
                                    <div key={idx}>
                                        <p className="font-medium">{instructor.name}</p>
                                        {instructor.email && (
                                            <div className="flex items-center space-x-2">
                                                <span className="text-xs text-gray-600">{instructor.email}</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </Drawer>
        </div>
    );
}

export default SectionCards;